import React from 'react';
import * as Inovx from '@shared';
import pako from 'pako';
import DiagnosticsStore from './../stores/DiagnosticsStore';
import DiagnosticsConstants from './../constants/DiagnosticsConstants';
import DiagnosticsActions from './../actions/DiagnosticsActions';
import DiagnosticsForm from './DiagnosticsForm';
var DateTime = Inovx.Shared.Common.DateTime;

var SystemActions = Inovx.Shared.System.Actions;
var SystemStore = Inovx.Shared.System.Store;
var UrlUtilities = Inovx.Shared.Common.UrlUtilities;

var DateTimeFormatHelper = Inovx.Shared.Common.DateTimeFormatHelper;

declare var InovxLoader: any;

var _pageNum = 1;
var _pageSize = 200;
var _sortDir = 1; //Descending
var _sortCol = 'Timestamp'; //Default
var _filterObj: { [id: string]: string } = {};

var _auditPageNum = 1;
var _auditSortDir = 1; //Descending
var _auditSortCol = 'Timestamp'; //Default
var _auditFilterObj: { [id: string]: string } = {};
var _displayAudit = false;

var _logLevelsEnabled = [
    { Option: "Debug", Value: 1, Checked: false, Default: false },
    { Option: "Information", Value: 2, Checked: true, Default: true },
    { Option: "Warning", Value: 3, Checked: true, Default: true },
    { Option: "Error", Value: 4, Checked: true, Default: true },
    { Option: "Fatal", Value: 5, Checked: true, Default: true }
];

var _auditLogLevelsEnabled = [
    { Option: "Audit", Value: 6, Checked: true, Default: true }
];

function localizeLogLevelOptions() {
    _logLevelsEnabled[0].Option = InovxLoader.t('Diagnostics:logLevels.debug');
    _logLevelsEnabled[1].Option = InovxLoader.t('Diagnostics:logLevels.information');
    _logLevelsEnabled[2].Option = InovxLoader.t('Diagnostics:logLevels.warning');
    _logLevelsEnabled[3].Option = InovxLoader.t('Diagnostics:logLevels.error');
    _logLevelsEnabled[4].Option = InovxLoader.t('Diagnostics:logLevels.fatal');

    _auditLogLevelsEnabled[0].Option = InovxLoader.t('Diagnostics:logLevels.audit');
}

// Updated for react-router-dom v5: component receives { match, location, history }
interface DiagnosticsRouteParams { messageId?: string }
interface DiagnosticsProps {
    match?: { params?: DiagnosticsRouteParams };
    location?: any;
    history?: any;
}

interface DiagnosticsState {
    isActionInProgress: boolean;
    diagnosticsData: ILogMessage[];
    auditData: ILogMessage[];
    modalVisible: boolean;
    auditVisible: boolean;
}

function toUtcDate(date: Date, time: string) {
    var newDate = new Date(Date.parse(date.toLocaleDateString() + " " + time));
    return newDate;
}

function getUtcTime(date: Date) {
    return date.getUTCHours() + ":" + date.getUTCMinutes();
}

function getState() {
    return {
        isActionInProgress: SystemStore.isActionInProgress(DiagnosticsConstants.GET_DATA) || SystemStore.isActionInProgress(DiagnosticsConstants.GET_NEXT_DATA) ||
            SystemStore.isActionInProgress(DiagnosticsConstants.GET_AUDIT_DATA) || SystemStore.isActionInProgress(DiagnosticsConstants.GET_NEXT_AUDIT_DATA),
        diagnosticsData: DiagnosticsStore.getData(),
        auditData: DiagnosticsStore.getAuditData(),
        lastPageRequest: _lastPageRequest,
        lastAuditRequest: _lastAuditPageRequest,
        modalVisible: DiagnosticsStore.getModalVisible(),
        auditVisible: _displayAudit

    }
}
var _lastPageRequest = 1;
var _lastAuditPageRequest = 1;

class DiagnosticsApp extends Inovx.Shared.ReactExt.BaseAppComponent<DiagnosticsProps, DiagnosticsState> {

    private static _timeFormat = "HH:mm";

    private _grid: any;
    private _auditGrid: any;

    constructor(props: DiagnosticsProps, context: any) {
        super('Diagnostics', props, context);

        this.state = getState();
    }

    protected _initialize = () => {
        _displayAudit = false;
        localizeLogLevelOptions();
        const messageId = this.props.match && this.props.match.params ? this.props.match.params.messageId : undefined;
        if (messageId) {
            _filterObj['MessageId'] = messageId;

            // If opening a specific log message, we want to include all message types,
            // including 'Debug'.
            _logLevelsEnabled[0].Checked = true;
        }

        DiagnosticsActions.GetData(
            this._getRequestObject(),
            () => {
                DiagnosticsActions.GetAuditData(
                    this._getAuditRequestObject(),
                    () => {
                        this._appLoaded = true
                    },
                    (error: any) => this._handlePermissionError(error), true);
            },
            (error: any) => this._handlePermissionError(error), true);
    }

    private _requestNextPage = (pageSize: number, pageNum: number, callback: any) => {
        var requestObject = this._getRequestObject();
        requestObject.PageNumber = pageNum;
        if (_lastPageRequest != pageNum) {
            DiagnosticsActions.GetNextData(requestObject, (data: any[]) => {
                _lastPageRequest = pageNum;
                if (data.length > 0) {
                    _pageNum = pageNum;
                }
                callback();
            }, () => callback());
        } else {
            callback();
        }
    }

    private _requestAuditNextPage = (pageSize: number, pageNum: number, callback: any) => {
        var requestObject = this._getAuditRequestObject();
        requestObject.PageNumber = pageNum;
        if (_lastAuditPageRequest != pageNum) {
            DiagnosticsActions.GetNextData(requestObject, (data: any[]) => {
                _lastAuditPageRequest = pageNum;
                if (data.length > 0) {
                    _auditPageNum = pageNum;
                }
                callback();
            }, () => callback());
        } else {
            callback();
        }
    }

    private _handleSort = (column: string, direction: string, callback: any) => {
        _pageNum = 1;
        _lastPageRequest = 1;

        if (column === 'DateDisplay' || column === 'TimeDisplay' || column === 'Date') {
            _sortCol = 'Timestamp';
        } else {
            _sortCol = column;
        }
        _sortDir = direction === 'asc' ? 0 : 1;
        DiagnosticsActions.GetData(this._getRequestObject(), () => {
            callback();
        }, () => callback());
    }

    private _handleAuditSort = (column: string, direction: string, callback: any) => {
        _auditPageNum = 1;
        _lastPageRequest = 1;

        if (column === 'DateDisplay' || column === 'TimeDisplay' || column === 'Date') {
            _auditSortCol = 'Timestamp';
        } else {
            _auditSortCol = column;
        }
        _auditSortDir = direction === 'asc' ? 0 : 1;
        DiagnosticsActions.GetAuditData(this._getAuditRequestObject(), () => {
            callback();
        }, () => callback());
    }
    private getFormatedDate = (date: any) => {

        const formatedDate = `${date.getFullYear()}-${(date.getMonth() + 1).toLocaleString('en-US', { minimumIntegerDigits: 2, useGrouping: false })}-${date.getDate().toLocaleString('en-US', { minimumIntegerDigits: 2, useGrouping: false })}`;
        return formatedDate;
    }
    private getFormatedTime = (date: any) => {
        const formatedTime = date.getUTCHours().toLocaleString('en-US', { minimumIntegerDigits: 2, useGrouping: false }) + ':' + date.getUTCMinutes().toLocaleString('en-US', { minimumIntegerDigits: 2, useGrouping: false });
        return formatedTime;
    }
    private _handleFilter = (filterObj: any, callback: any) => {
        _pageNum = 1;
        _lastPageRequest = 1;

        _filterObj = {};

        for (var k in filterObj) {
            if (typeof filterObj[k] === 'string') {
                _filterObj[k] = filterObj[k];
            } else if (typeof filterObj[k] === 'object') {
                if (filterObj[k]) {
                    switch (k) {
                        case 'LogLevel':
                            _logLevelsEnabled = filterObj[k];
                            break;
                        case 'TimeDisplay':
                            if (filterObj[k]['start']) {
                                let startTimeVal = filterObj[k]['start'] as string;
                                _filterObj['StartTime'] = Inovx.Shared.Common.DateTime.convertTimeFormats(startTimeVal,
                                    {
                                        srcTimeFormat: DiagnosticsApp._timeFormat,
                                        dstTimeFormat: DiagnosticsApp._timeFormat,
                                        srcTimeZone: Inovx.Shared.Common.DateTime.utcTimeZone
                                    });
                                _filterObj['RawStartTime'] = filterObj[k]['start'] as string;
                            }
                            if (filterObj[k]['end']) {
                                let endTimeVal = filterObj[k]['end'] as string;
                                _filterObj['EndTime'] = Inovx.Shared.Common.DateTime.convertTimeFormats(endTimeVal,
                                    {
                                        srcTimeFormat: DiagnosticsApp._timeFormat,
                                        dstTimeFormat: DiagnosticsApp._timeFormat,
                                        srcTimeZone: Inovx.Shared.Common.DateTime.utcTimeZone
                                    });
                                _filterObj['RawEndTime'] = filterObj[k]['end'] as string;
                            }
                            break;
                        case 'Date':
                            if (filterObj[k]['start']) {

                                var startDate = new Date(Date.parse(filterObj[k]['start']));

                                _filterObj['StartDate'] = this.getFormatedDate(startDate);

                                let startTimeVal = filterObj[k]['start'] as string;
                                let tD = new DateTime({ date: startTimeVal });

                                var t1 = DateTimeFormatHelper.getFormatTime(startDate.toUTCString(), false, true);
                                var t2 = DateTimeFormatHelper.getFormatTime(startDate.toString(), false, false);

                                _filterObj['StartTime'] = this.getFormatedTime(startDate);
                                _filterObj['RawStartTime'] = t2;
                            }
                            if (filterObj[k]['end']) {
                                var endDate = new Date(Date.parse(filterObj[k]['end']));

                                _filterObj['EndDate'] = this.getFormatedDate(endDate);

                                var t1 = DateTimeFormatHelper.getFormatTime(endDate.toString(), false, true);
                                var t2 = DateTimeFormatHelper.getFormatTime(endDate.toString(), false, false);
                                _filterObj['EndTime'] = this.getFormatedTime(endDate);
                                _filterObj['RawEndTime'] = t2;
                            }
                            break;
                        case 'DateDisplay':
                            if (filterObj[k]['start']) {
                                var startDate = new Date(Date.parse(filterObj[k]['start']));
                                _filterObj['StartDate'] = `${startDate.getFullYear()}-${startDate.getMonth() + 1}-${startDate.getDate()}`;
                                _filterObj['RawStartDate'] = filterObj[k]['start'] as string;
                            }
                            if (filterObj[k]['end']) {
                                var endDate = new Date(Date.parse(filterObj[k]['end']));
                                _filterObj['EndDate'] = `${endDate.getFullYear()}-${endDate.getMonth() + 1}-${endDate.getDate()}`;
                                _filterObj['RawEndDate'] = filterObj[k]['end'] as string;
                            }
                            break;
                    }
                }
            }
        }

        DiagnosticsActions.GetData(this._getRequestObject(), () => {
            callback();
        }, () => callback());
    }

    private _handleAuditFilter = (filterObj: any, callback: any) => {
        _auditPageNum = 1;
        _lastAuditPageRequest = 1;

        _auditFilterObj = {};

        for (var k in filterObj) {
            if (typeof filterObj[k] === 'string') {
                _auditFilterObj[k] = filterObj[k];
            } else if (typeof filterObj[k] === 'object') {
                if (filterObj[k]) {
                    switch (k) {
                        case 'TimeDisplay':
                            if (filterObj[k]['start']) {
                                let startTimeVal = filterObj[k]['start'] as string;

                                var startDate = new Date(Date.parse(filterObj[k]['start']));
                                _auditFilterObj['StartDate'] = `${startDate.getFullYear()}-${startDate.getMonth() + 1}-${startDate.getDate()}`;
                                _auditFilterObj['RawStartDate'] = filterObj[k]['start'] as string;

                                var t1 = DateTimeFormatHelper.getFormatTime(startDate.toString(), false, true);
                                var t2 = DateTimeFormatHelper.getFormatTime(startDate.toString(), false, false);
                                _auditFilterObj['StartTime'] = startDate.getUTCHours() + ':' + startDate.getUTCMinutes();
                                _auditFilterObj['RawStartTime'] = t2;
                            }
                            if (filterObj[k]['end']) {
                                let endTimeVal = filterObj[k]['end'] as string;

                                var endDate = new Date(Date.parse(filterObj[k]['end']));
                                _auditFilterObj['EndDate'] = `${endDate.getFullYear()}-${endDate.getMonth() + 1}-${endDate.getDate()}`;
                                _auditFilterObj['RawEndDate'] = filterObj[k]['end'] as string;

                                var t1 = DateTimeFormatHelper.getFormatTime(endDate.toString(), false, true);
                                var t2 = DateTimeFormatHelper.getFormatTime(endDate.toString(), false, false);
                                _auditFilterObj['EndTime'] = endDate.getUTCHours() + ':' + endDate.getUTCMinutes();
                                _auditFilterObj['RawEndTime'] = t2;
                            }
                            break;
                        case 'Date':
                            if (filterObj[k]['start']) {
                                var startDate = new Date(Date.parse(filterObj[k]['start']));

                                _auditFilterObj['StartDate'] = this.getFormatedDate(startDate);
                                _auditFilterObj['RawStartDate'] = filterObj[k]['start'] as string;

                                var t1 = DateTimeFormatHelper.getFormatTime(startDate.toString(), false, true);
                                var t2 = DateTimeFormatHelper.getFormatTime(startDate.toString(), false, false);
                                _auditFilterObj['StartTime'] = this.getFormatedTime(startDate);
                                _auditFilterObj['RawStartTime'] = t2;
                            }
                            if (filterObj[k]['end']) {
                                var endDate = new Date(Date.parse(filterObj[k]['end']));

                                _auditFilterObj['EndDate'] = this.getFormatedDate(endDate);
                                _auditFilterObj['RawEndDate'] = filterObj[k]['end'] as string;

                                var t1 = DateTimeFormatHelper.getFormatTime(endDate.toString(), false, true);
                                var t2 = DateTimeFormatHelper.getFormatTime(endDate.toString(), false, false);
                                _auditFilterObj['EndTime'] = this.getFormatedTime(endDate);
                                _auditFilterObj['RawEndTime'] = t2;
                            }
                            break;
                        case 'DateDisplay':
                            if (filterObj[k]['start']) {
                                var startDate = new Date(Date.parse(filterObj[k]['start']));
                                _auditFilterObj['StartDate'] = `${startDate.getFullYear()}-${startDate.getMonth() + 1}-${startDate.getDate()}`;
                                _auditFilterObj['RawStartDate'] = filterObj[k]['start'] as string;

                            }
                            if (filterObj[k]['end']) {
                                var endDate = new Date(Date.parse(filterObj[k]['end']));
                                _auditFilterObj['EndDate'] = `${endDate.getFullYear()}-${endDate.getMonth() + 1}-${endDate.getDate()}`;
                                _auditFilterObj['RawEndDate'] = filterObj[k]['end'] as string;
                            }
                            break;
                    }
                }
            }
        }

        DiagnosticsActions.GetAuditData(this._getAuditRequestObject(), () => {
            callback();
        }, () => callback());
    }

    private _displayDiagnostics = (e: Event, diagnostic: any) => {

        DiagnosticsActions.DisplayDiagnosticInformation(diagnostic.MessageId);
    }

    private _onRowClick = (e: any, rowData: any) => {
        if (e.ctrlKey) {
            this._grid.handleClearFilters({ SessionId: rowData.SessionId, LogLevel: "Debug,Information,Warning,Error,Fatal" });
        }
        else {
            DiagnosticsActions.DisplayDiagnosticInformation(rowData.MessageId);
        }
    }

    private _onAuditRowClick = (e: any, rowData: any) => {
        if (e.ctrlKey) {
            this._auditGrid.handleClearFilters({ SessionId: rowData.SessionId, LogLevel: "Audit" });
        }
        else {
            DiagnosticsActions.DisplayDiagnosticInformation(rowData.MessageId);
        }
    }

    private _refreshData = () => {
        this._grid.handleRefresh();
    }

    private _refreshAuditData = () => {
        this._auditGrid.handleRefresh();
    }

    private _exportCSV = () => {
        var requestObject = this._getAuditRequestObject();
        DiagnosticsActions.ExportToCSV(requestObject, this._exportToCSVCallback, (data: any) => {

            let binary = atob(data);
            let bytes = Uint8Array.from(binary, c => c.charCodeAt(0));

            let inflated = pako.inflate(bytes);
            let csv = String.fromCharCode.apply(null, inflated as any);

            let csvContent = "data:text/csv;charset=utf-8,"
                + csv;
            var encodedUri = encodeURI(csvContent);
            var link = document.createElement("a");
            link.setAttribute("href", encodedUri);
            link.setAttribute("download", "audit_log.csv");
            document.body.appendChild(link);

            link.click();
        });
    }

    private _toggleDataTransferFilter = () => {
        this._grid.setFilter("Source", "VSuiteDataTransfer");
    }

    private _clearGridFilters = () => {
        this._grid.handleClearFilters();
    }

    private _clearAuditGridFilters = () => {
        this._auditGrid.handleClearFilters();
    }

    private _getRequestObject = (): LogRequestObject => {
        const _logLevelValues = _logLevelsEnabled.map((logLevel) => { if (logLevel.Checked) return logLevel.Value });

        return {
            MaxResultsPerPage: _pageSize,
            PageNumber: _pageNum,
            SortBy: _sortCol,
            SortDirection: _sortDir,
            ColumnFilters: _filterObj,
            LogLevel: _logLevelValues
        };
    }

    private _getAuditRequestObject = (): LogRequestObject => {
        const _logLevelValues = _auditLogLevelsEnabled.map((logLevel) => { if (logLevel.Checked) return logLevel.Value });
        return {
            MaxResultsPerPage: _pageSize,
            PageNumber: _auditPageNum,
            SortBy: _auditSortCol,
            SortDirection: _auditSortDir,
            ColumnFilters: _auditFilterObj,
            LogLevel: _logLevelValues
        };
    }

    private _onChange = () => {
        this.setState(getState());
    }

    private _onClickDiagnostics = () => {
        _displayAudit = false;
        this.setState({ auditVisible: false });
    }

    private _onClickAudit = () => {
        _displayAudit = true;
        this.setState({ auditVisible: true});
    }

    private _onModalClose = () => {

    }
    componentDidMount() {
        this.componentWillMountBase();
        DiagnosticsStore.addChangeListener(this._onChange);
        SystemStore.addChangeListener(this._onChange);
    }

    componentWillUnmount() {
        DiagnosticsStore.removeChangeListener(this._onChange);
        SystemStore.removeChangeListener(this._onChange);
    }

    private _exportToCSVCallback = (inProgress: boolean) => {
        if (inProgress)
            SystemActions.beginAction(DiagnosticsConstants.EXPORT_TO_CSV);
        else
            SystemActions.endAction(DiagnosticsConstants.EXPORT_TO_CSV);
        this.setState({});
    }

    public render() {
        var iconHeaderProps = {
            iconUrl: `${UrlUtilities.getBaseApplicationUrl()}/images/Diagnostics.png`,
            iconAtt: { alt: InovxLoader.t('Diagnostics:header.diagnostics') },
            title: InovxLoader.t('Diagnostics:header.diagnostics')
        }

        var displayAudit = this.state.auditVisible;

        var diagnosticsColumns = [{
            id: "LogLevel",
            name: InovxLoader.t('Diagnostics:grid.level'),
            width: 115,
            filterType: "list",
            filteringOptions: _logLevelsEnabled,
            enableSort: true
        }, {
            id: "Date",
            name: InovxLoader.t('Diagnostics:grid.dateTime'),
            width: 200,
            filterType: 'datetime',
            filterValue: { start: _filterObj["StartDate"], end: _filterObj["EndDate"] },
            enableSort: true
        }, {
            id: "MessageId",
            name: InovxLoader.t('Diagnostics:grid.messageID'),
            width: 320,
            filterType: 'string',
            filterValue: _filterObj["MessageId"],
            enableSort: true
        }, {
            id: "Message",
            name: InovxLoader.t('Diagnostics:grid.message'),
            width: 200,
            filterType: 'string',
            filterValue: _filterObj["Message"],
            enableSort: true
        }, {
            id: "Caption",
            name: InovxLoader.t('Diagnostics:grid.detail'),
            width: 450,
            filterType: 'string',
            filterValue: _filterObj["Caption"],
            enableSort: true
        }, {
            id: "Source",
            name: InovxLoader.t('Diagnostics:grid.source'),
            width: 160,
            filterType: 'string',
            filterValue: _filterObj["Source"],
            enableSort: true
        }, {
            id: "Service",
            name: InovxLoader.t('Diagnostics:grid.service'),
            width: 230,
            filterType: 'string',
            filterValue: _filterObj["Service"],
            enableSort: true
        }, {
            id: "SessionId",
            name: InovxLoader.t('Diagnostics:grid.sessionID'),
            width: 200,
            filterType: 'string',
            filterValue: _filterObj["SessionId"],
            enableSort: true
        }, {
            id: "HostName",
            name: InovxLoader.t('Diagnostics:grid.hostName'),
            width: 170,
            filterType: 'string',
            filterValue: _filterObj["HostName"],
            enableSort: true
        }, {
            id: "ClientIp",
            name: InovxLoader.t('Diagnostics:grid.clientIP'),
            width: 150,
            filterType: 'string',
            filterValue: _filterObj["ClientIp"],
            enableSort: true
        }, {
            id: "ClientUserName",
            name: InovxLoader.t('Diagnostics:grid.initiatedby'),
            width: 150,
            filterType: 'string',
            filterValue: _filterObj["ClientUserName"],
            enableSort: true
        }, {
            id: "Version",
            name: InovxLoader.t('Diagnostics:grid.version'),
            width: 120,
            filterType: 'string',
            filterValue: _filterObj["Version"],
            enableSort: true
            }];

        var auditColumns = [{
            id: "Date",
            name: InovxLoader.t('Diagnostics:grid.dateTime'),
            width: 200,
            filterType: 'datetime',
            filterValue: { start: _auditFilterObj["StartDate"], end: _auditFilterObj["EndDate"] },
            enableSort: true
        },{
            id: "MessageId",
            name: InovxLoader.t('Diagnostics:grid.messageID'),
            width: 320,
            filterType: 'string',
            filterValue: _auditFilterObj["MessageId"],
            enableSort: true
        }, {
            id: "Message",
            name: InovxLoader.t('Diagnostics:grid.message'),
            width: 280,
            filterType: 'string',
            filterValue: _auditFilterObj["Message"],
            enableSort: true
        }, {
            id: "Caption",
            name: InovxLoader.t('Diagnostics:grid.detail'),
            width: 400,
            filterType: 'string',
            filterValue: _auditFilterObj["Caption"],
            enableSort: true
        }, {
            id: "HostName",
            name: InovxLoader.t('Diagnostics:grid.hostName'),
            width: 140,
            filterType: 'string',
            filterValue: _auditFilterObj["HostName"],
            enableSort: true
        }, {
            id: "ClientIp",
            name: InovxLoader.t('Diagnostics:grid.clientIP'),
            width: 120,
            filterType: 'string',
            filterValue: _auditFilterObj["ClientIp"],
            enableSort: true
        }, {
            id: "ClientUserName",
            name: InovxLoader.t('Diagnostics:grid.initiatedby'),
            width: 160,
            filterType: 'string',
            filterValue: _auditFilterObj["ClientUserName"],
            enableSort: true
        }];

        return (
            <Inovx.Shared.Core.ReactLoader appReady={this.isAppLoaded()} errorStatus={this._errorStatus}>
                <Inovx.Shared.Controls.ActionInProgressLoader actionKey={[DiagnosticsConstants.EXPORT_TO_CSV]} class={'temp-modal-loader'}>
                    <div className="action-in-progress-loader-lrg"></div>
                </Inovx.Shared.Controls.ActionInProgressLoader>
                <div className="diagnostics-app-content">
                    <Inovx.Shared.Layout.IconHeader {...iconHeaderProps} />
                    <ul className="list-inline">
                        <li className={displayAudit ? "diagnostics-tab-inactive" : "diagnostics-tab-active"}><a href="#" onClick={this._onClickDiagnostics}>{InovxLoader.t("Diagnostics:grid.diagnostics")}</a></li>
                        <li className={displayAudit ? "diagnostics-tab-active" : "diagnostics-tab-inactive"}><a href="#" onClick={this._onClickAudit}>{InovxLoader.t("Diagnostics:grid.audit")}</a></li>
                    </ul>

                    {!displayAudit &&
                        <div>
                            <div className="diagnostics-actions-container">
                                <button id="diagApp-refresh-btn" className="btn btn-primary diagnostics-filter-btn" onClick={this._refreshData}>{InovxLoader.t("Diagnostics:filter.refresh")}</button>
                                <button id="diagApp-data-transfer-btn"className="btn btn-primary diagnostics-filter-btn" onClick={this._toggleDataTransferFilter}>{InovxLoader.t("Diagnostics:filter.dataTransfer")}</button>
                                <button id="diagApp-clear-btn" className="btn btn-primary diagnostics-filter-btn" onClick={this._clearGridFilters}>{InovxLoader.t("Diagnostics:filter.clear")}</button>
                            </div>
                                <Inovx.Shared.Controls.FluxGrid
                                    dataSource={this.state.diagnosticsData}
                                    columns={diagnosticsColumns}
                                    requestData={this._requestNextPage}
                                    pageNum={_pageNum}
                                    pageSize={_pageSize}
                                    keyColumn='MessageId'
                                    loading={this.state.isActionInProgress}
                                    handleSort={this._handleSort}
                                    handleFilter={this._handleFilter}
                                    height={475}
                                    fontSize={"0.8em"}
                                    ref={(grid: any) => { this._grid = grid; }}
                                    onRowClick={this._onRowClick}
                                    />

                            <Inovx.Shared.Controls.ModalPopUp isOpen={this.state.modalVisible} >
                                <DiagnosticsForm title={InovxLoader.t("Diagnostics:header.formDiagnostics")} />
                                </Inovx.Shared.Controls.ModalPopUp>
                            </div>
                    }

                    {displayAudit &&
                        <div>
                            <div className="diagnostics-actions-container">
                                <button id="diagApp-audit-refresh-btn" className="btn btn-primary diagnostics-filter-btn" onClick={this._refreshAuditData}>{InovxLoader.t("Diagnostics:filter.refresh")}</button>
                                <button id="diagApp-audit-clear-btn" className="btn btn-primary diagnostics-filter-btn" onClick={this._clearAuditGridFilters}>{InovxLoader.t("Diagnostics:filter.clear")}</button>
                                <button id="diagApp-data-export-btn" className="btn btn-primary diagnostics-filter-btn" onClick={this._exportCSV}>{InovxLoader.t("Diagnostics:filter.export")}</button>
                            </div>
                            <Inovx.Shared.Controls.FluxGrid
                                dataSource={this.state.auditData}
                                columns={auditColumns}
                                requestData={this._requestAuditNextPage}
                                pageNum={_auditPageNum}
                                pageSize={_pageSize}
                                keyColumn='MessageId'
                                loading={this.state.isActionInProgress}
                                handleSort={this._handleAuditSort}
                                handleFilter={this._handleAuditFilter}
                                height={475}
                                fontSize={"0.8em"}
                                ref={(grid: any) => { this._auditGrid = grid; }}
                                onRowClick={this._onAuditRowClick}
                            />
                            <Inovx.Shared.Controls.ModalPopUp isOpen={this.state.modalVisible} >
                                <DiagnosticsForm title={InovxLoader.t("Diagnostics:header.formAudit")} />
                            </Inovx.Shared.Controls.ModalPopUp>
                        </div>
                    }
                </div>
            </Inovx.Shared.Core.ReactLoader>
        );
    }
}

export default DiagnosticsApp;
